import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { connect } from 'react-redux';
import { Button, FieldSelect, H2, IconSpinner, LayoutComposer, NamedLink } from '../../components';
import {
  conditionMapping,
  getPaddle,
  getPaddleMakes,
  getPaddleModels,
} from '../../config/configPaddleGenius';
import appSettings from '../../config/settings';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import FooterContainer from '../FooterContainer/FooterContainer';
import StaticPage from '../PageBuilder/StaticPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import css from './PaddleGeniusPage.module.css';

const PaddleGeniusPageComponent = ({ pageAssetsData, inProgress, intl, currentUser }) => {
  const pageTitle = pageAssetsData?.paddleGeniusPage?.data?.meta?.pageTitle?.content;
  const pageDescription = pageAssetsData?.paddleGeniusPage?.data?.meta?.pageDescription?.content;
  const socialSharing = pageAssetsData?.paddleGeniusPage?.data?.meta?.socialSharing;

  const layoutAreas = `
    topbar
    main
    footer
  `;

  const [paddle, setPaddle] = useState();
  const [condition, setCondition] = useState();

  const formatter = useMemo(() => {
    return Intl.NumberFormat('en-US', appSettings.getCurrencyFormatting('USD'));
  }, []);

  return (
    <StaticPage
      className={css.root}
      title={pageTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: socialSharing?.description ?? pageDescription,
        name: socialSharing?.title ?? pageTitle,
      }}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer />
              </Topbar>
              {inProgress ? (
                <div className={css.loading}>
                  <IconSpinner delay={600} />
                </div>
              ) : (
                <Main className={css.main}>
                  <FinalForm
                    onSubmit={values => {
                      setPaddle(getPaddle(values.make, values.model));
                      setCondition(values.condition);
                    }}
                  >
                    {props => (
                      <form className={css.card} onSubmit={props.handleSubmit}>
                        <header className={css.header}>
                          <H2 className={css.title}>{pageTitle}</H2>
                          <p className={css.description}>{pageDescription}</p>
                        </header>
                        <FieldSelect
                          id="PaddleGeniusPage.make"
                          name="make"
                          label={intl.formatMessage({ id: 'PaddleGeniusPage.makeLabel' })}
                          validate={validators.required(
                            intl.formatMessage({ id: 'PaddleGeniusPage.requiredMakeFailed' })
                          )}
                          onChange={() => {
                            props.form.change('model', '');
                            props.form.resetFieldState('model');
                            props.form.change('condition', '');
                            props.form.resetFieldState('condition');
                          }}
                        >
                          <option value="" disabled>
                            {intl.formatMessage({ id: 'PaddleGeniusPage.makePlaceholder' })}
                          </option>
                          {getPaddleMakes().map(make => (
                            <option value={make} key={make}>
                              {make}
                            </option>
                          ))}
                        </FieldSelect>
                        <FormSpy subscription={{ values: true, errors: true }}>
                          {({ values, errors }) => {
                            const models = getPaddleModels(values['make']);

                            return (
                              <FieldSelect
                                id="PaddleGeniusPage.model"
                                name="model"
                                label={intl.formatMessage({ id: 'PaddleGeniusPage.modelLabel' })}
                                disabled={!!errors['make']}
                                validate={validators.required(
                                  intl.formatMessage({ id: 'PaddleGeniusPage.requiredModelFailed' })
                                )}
                              >
                                <option value="" disabled>
                                  {intl.formatMessage({ id: 'PaddleGeniusPage.modelPlaceholder' })}
                                </option>
                                {models.map(model => (
                                  <option value={model} key={model}>
                                    {model}
                                  </option>
                                ))}
                              </FieldSelect>
                            );
                          }}
                        </FormSpy>

                        <FormSpy subscription={{ errors: true }}>
                          {({ errors }) => {
                            return (
                              <FieldSelect
                                id="PaddleGeniusPage.condition"
                                name="condition"
                                label={intl.formatMessage({
                                  id: 'PaddleGeniusPage.conditionLabel',
                                })}
                                disabled={!!errors['make'] || !!errors['model']}
                                validate={validators.required(
                                  intl.formatMessage({
                                    id: 'PaddleGeniusPage.requiredConditionFailed',
                                  })
                                )}
                              >
                                <option value="" disabled>
                                  {intl.formatMessage({
                                    id: 'PaddleGeniusPage.conditionPlaceholder',
                                  })}
                                </option>
                                <option value="New">{conditionMapping['New']}</option>
                                <option value="Like New">{conditionMapping['Like New']}</option>
                                <option value="Good">{conditionMapping['Good']}</option>
                              </FieldSelect>
                            );
                          }}
                        </FormSpy>
                        <Button type="subimt">
                          <FormattedMessage id="PaddleGeniusPage.calculateButtonLabel" />
                        </Button>
                      </form>
                    )}
                  </FinalForm>
                  {paddle ? (
                    <div className={classNames(css.card, css.result)}>
                      <img
                        src={`/static/paddles/${paddle['Photo ID']}.png`}
                        alt={`${paddle['Make']} ${paddle['Model']} ${condition}`}
                      />
                      <div>
                        <H2 className={css.title}>
                        <FormattedMessage id="PaddleGeniusPage.title" />
                        </H2>
                        <table>
                          <caption>
                            <FormattedMessage id="PaddleGeniusPage.breakdownCaption" />
                          </caption>
                          <tbody>
                            <tr>
                              <th>
                                <FormattedMessage id="PaddleGeniusPage.breakdownMake" />
                              </th>
                              <td>{paddle['Make']}</td>
                            </tr>
                            <tr>
                              <th>
                                <FormattedMessage id="PaddleGeniusPage.breakdownModel" />
                              </th>
                              <td>{paddle['Model']}</td>
                            </tr>
                            <tr>
                              <th>
                                <FormattedMessage id="PaddleGeniusPage.breakdownCondition" />
                              </th>
                              <td>{conditionMapping[condition]}</td>
                            </tr>
                            <tr>
                              <th>
                                <FormattedMessage id="PaddleGeniusPage.breakdownPrice" />
                              </th>
                              <td>{formatter.format(paddle[condition])}</td>
                            </tr>
                          </tbody>
                        </table>
                        <NamedLink
                          name="NewListingPageWithParams"
                          params={{
                            id: '00000000-0000-0000-0000-000000000000',
                            slug: 'draft',
                            type: 'new',
                            tab: 'details',
                            brand: paddle['Make'],
                            model: paddle['Model'],
                            condition: conditionMapping[condition],
                          }}
                          className={css.link}
                        >
                          <Button
                            disabled={!currentUser}
                            className={classNames(css.button, currentUser && css.buttonActive)}
                          >
                            <FormattedMessage id="PaddleGeniusPage.listButtonLabel" />
                          </Button>
                        </NamedLink>
                        {!currentUser && (
                          <p className={css.accountRequired}>
                            <FormattedMessage id="PaddleGeniusPage.accountRequired" />
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </Main>
              )}
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  return { pageAssetsData, inProgress, error, currentUser: state?.user.currentUser };
};

const PaddleGeniusPage = injectIntl(connect(mapStateToProps)(PaddleGeniusPageComponent));

export default PaddleGeniusPage;
